import {
	MOBILE_BOTTOM_DOCK_THRESHOLDS,
	createMobileBottomDockStateMachine,
	type MobileBottomDockSnapshot,
	type MobileBottomDockStateMachine,
} from './mobileBottomDockStateMachine';
import { mountedFor } from './mediaPlayer';
import { getRuntime } from './runtimeAccessor';
import { showArticle } from './articleController';
import { restoreFocusWithoutRing } from './modalAccessibility';
import {
	SHEET_CLICK_SUPPRESS_MS,
	SHEET_EDGE_PULL_ARM_PX,
	SHEET_SETTLE_MS,
	shouldDismissSheet,
} from './sheetDismissPolicy';

export interface MobileBottomDockController {
	machine: MobileBottomDockStateMachine;
	snapshot(): MobileBottomDockSnapshot;
	openNowPlaying(): void;
	closeNowPlaying(): void;
	historySnapshot(): MobileBottomDockHistorySnapshot;
	restoreHistorySnapshot(snapshot: MobileBottomDockHistorySnapshot): void;
	finishHistoryRestore(): void;
	destroy(): void;
}

export interface MobileBottomDockHistorySnapshot {
	dockState: Exclude<MobileBottomDockSnapshot['dockState'], 'now-playing'>;
	scrollY: number;
}

type ElementPosition = { parent: Node; nextSibling: Node | null };

const mountedControllers = new WeakMap<HTMLElement, MobileBottomDockController>();
const miniOpenGestureExclusionSelector = 'input, select, textarea, [contenteditable], .wp-progress, [role="slider"]';
const bottomOpenGestureScopeSelector = '.web-player, .tab-nav';
const nowPlayingDismissGestureExclusionSelector = 'input, select, textarea, [contenteditable], .wp-progress, [role="slider"]';
/**
 * Collapsing the dock shrinks .app-main's bottom padding by --bottom-chrome-height
 * (56px), which reduces the document height. When the user is within that delta of
 * the bottom the browser clamps scrollY down and the state machine reads the
 * correction as an up-scroll, flipping the dock back to expanded and stranding the
 * viewport short of the bottom with the chrome overlapping the last content.
 * headroom = collapse delta (56) + expand threshold (16) + margin (24).
 */
const COLLAPSE_HEADROOM_PX = 96;

export function hasCollapseHeadroom(win: Window, headroomPx = COLLAPSE_HEADROOM_PX): boolean {
	const doc = win.document;
	const scroller = doc.scrollingElement ?? doc.documentElement;
	// Safari's layout viewport (innerHeight) can remain taller than the visible
	// viewport while its address/tab chrome is expanded. Use visualViewport
	// when present so compacting cannot remove the final article clearance and
	// strand Previous / All chapters underneath the dock.
	const viewportHeight = win.visualViewport?.height ?? win.innerHeight;
	const maxScroll = scroller.scrollHeight - viewportHeight;
	return win.scrollY < maxScroll - headroomPx;
}

export function mountMobileBottomDockController(root: HTMLElement): MobileBottomDockController {
	const existing = mountedControllers.get(root);
	if (existing) return existing;
	const doc = root.ownerDocument;
	const win = doc.defaultView;
	const player = root.querySelector<HTMLElement>('#webPlayer');
	const tabNav = root.querySelector<HTMLElement>('#tabNav');
	if (!player || !tabNav || !win) return inertController();
	const activeBeforeMount = doc.activeElement instanceof HTMLElement && (player.contains(doc.activeElement) || tabNav.contains(doc.activeElement))
		? doc.activeElement
		: null;

	const playerPosition = rememberPosition(player);
	const tabPosition = rememberPosition(tabNav);
	const dock = doc.createElement('div');
	dock.className = 'mobile-bottom-dock';
	dock.setAttribute('aria-label', 'Playback and primary navigation');
	tabNav.parentNode?.insertBefore(dock, tabNav);
	dock.appendChild(player);
	dock.appendChild(tabNav);
	// Scripted re-park after the dock reshuffles the DOM: restore where the
	// user was without painting a device keyboard-access halo.
	restoreFocusWithoutRing(activeBeforeMount);

	const appLayout = root.querySelector<HTMLElement>('.app-layout');
	const videoFullscreen = root.querySelector<HTMLElement>('#videoFullscreen');
	const progress = player.querySelector<HTMLElement>('.wp-progress');
	const miniInfo = player.querySelector<HTMLElement>('.wp-info');
	const nowPlayingSurface = player.querySelector<HTMLElement>('.wp-now-playing');
	const nowPlayingGesture = player.querySelector<HTMLElement>('.wp-now-playing-gesture');
	const nowPlayingHero = player.querySelector<HTMLElement>('.wp-now-playing-hero');
	const nowPlayingPlaylist = player.querySelector<HTMLElement>('.wp-playlist');
	const nowPlayingScrollRegion = player.querySelector<HTMLElement>('.wp-now-playing-scroll');
	const nowPlayingControls = player.querySelector<HTMLElement>('.wp-controls');
	const closeButton = player.querySelector<HTMLElement>('.wp-now-playing-close');
	const machine = createMobileBottomDockStateMachine(readScrollY(win));
	const mobileQuery = win.matchMedia?.('(max-width: 1024px)');
	let mobileViewport = mobileQuery?.matches ?? true;
	let bottomOpenGesture: { pointerId: number; startX: number; startY: number; source: Element; horizontal: boolean } | null = null;
	let suppressDockClickUntil = 0;
	let suppressDockClickSource: Element | null = null;
	let nowPlayingStartY: number | null = null;
	let nowPlayingStartX: number | null = null;
	let nowPlayingGestureSource: Element | null = null;
	let nowPlayingHorizontalGesture = false;
	let nowPlayingStartScrollTop = 0;
	let suppressNowPlayingClickUntil = 0;
	let suppressNowPlayingClickSource: Element | null = null;
	let playlistPullGesture: {
		identifier: number;
		startX: number;
		startY: number;
		source: Element;
		scrollRegion: HTMLElement;
		active: boolean;
		horizontal: boolean;
		edgeStartY: number | null;
		lastY: number;
		lastAt: number;
		velocity: number;
	} | null = null;
	let nowPlayingDragging = false;
	let nowPlayingDragDy = 0;
	let lastMoveY = 0;
	let lastMoveAt = 0;
	let moveVelocity = 0;
	let settling = false;
	let nowPlayingSettleTimer: ReturnType<typeof setTimeout> | null = null;
	let nowPlayingOpenFrame: number | null = null;
	let nowPlayingOpenCommitFrame: number | null = null;
	let nowPlayingDismissFrame: number | null = null;
	let nowPlayingReturnTimer: ReturnType<typeof setTimeout> | null = null;
	let backgroundInertState: Map<HTMLElement, boolean> | null = null;
	let modalReturnFocus: HTMLElement | null = null;
	let destroyed = false;
	let restoringHistory = false;
	let miniInfoStart: { pointerId: number; x: number; y: number; moved: boolean } | null = null;

	const syncDom = () => {
		const snapshot = machine.snapshot();
		// A cancelled iOS pointer sequence can skip the now-playing settle timer.
		// Do not let its inline translateY survive after the machine has returned
		// to the mini dock, where it would move the fixed chrome into the reader.
		if (
			snapshot.dockState !== 'now-playing'
			&& !dock.classList.contains('now-playing-returning')
			&& !dock.classList.contains('now-playing-dismissed')
		) {
			dock.style.removeProperty('transform');
			dock.classList.remove('now-playing-dragging', 'now-playing-settling', 'now-playing-opening');
		}
		dock.dataset.dockState = snapshot.dockState;
		dock.dataset.videoHidden = String(snapshot.hidden);
		dock.dataset.playerVisible = String(player.classList.contains('visible'));
		appLayout?.setAttribute('data-mobile-dock-state', snapshot.dockState);
		appLayout?.setAttribute('data-mobile-player-visible', String(player.classList.contains('visible')));
		const shouldLock = mobileViewport && snapshot.scrollLocked;
		doc.documentElement.classList.toggle('mobile-now-playing-open', shouldLock);
		doc.body.classList.toggle('mobile-now-playing-open', shouldLock);
	};
	const activateModal = () => {
		if (backgroundInertState) return;
		nowPlayingSurface?.setAttribute('role', 'dialog');
		nowPlayingSurface?.setAttribute('aria-modal', 'true');
		const activeElement = doc.activeElement;
		modalReturnFocus = activeElement instanceof HTMLElement && activeElement !== doc.body ? activeElement : null;
		backgroundInertState = new Map();
		for (const element of root.children) {
			// Lazy business overlays live in this SSR portal. Keep the portal out
			// of the dock's background isolation so an overlay opened from Now
			// Playing does not inherit an inert ancestor.
			if (!(element instanceof HTMLElement) || element === dock || element.matches('[data-overlay-portal]')) continue;
			backgroundInertState.set(element, element.hasAttribute('inert'));
			element.setAttribute('inert', '');
		}
	};
	const releaseModal = () => {
		nowPlayingSurface?.removeAttribute('role');
		nowPlayingSurface?.removeAttribute('aria-modal');
		if (!backgroundInertState) return null;
		for (const [element, wasInert] of backgroundInertState) {
			if (wasInert) element.setAttribute('inert', '');
			else element.removeAttribute('inert');
		}
		backgroundInertState = null;
		const returnFocus = modalReturnFocus;
		modalReturnFocus = null;
		return returnFocus;
	};
	const syncNowPlayingLayout = () => {
		if (!nowPlayingSurface || !nowPlayingPlaylist || machine.snapshot().dockState !== 'now-playing') return;
		const rows = Array.from(nowPlayingPlaylist.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.classList.contains('wp-playlist-row'));
		const blockSize = (element: HTMLElement | null, fallback: number) => {
			if (!element) return 0;
			const style = win.getComputedStyle(element);
			if (style.display === 'none') return 0;
			const rectHeight = element.getBoundingClientRect().height;
			if (rectHeight > 0) return rectHeight;
			const cssHeight = Number.parseFloat(style.height);
			return Number.isFinite(cssHeight) && cssHeight > 0 ? cssHeight : fallback;
		};
		const verticalSpace = (element: HTMLElement | null, properties: Array<'marginTop' | 'marginBottom' | 'paddingTop' | 'paddingBottom'>) => {
			if (!element) return 0;
			const style = win.getComputedStyle(element);
			if (style.display === 'none') return 0;
			return properties.reduce((total, property) => {
				const value = Number.parseFloat(style[property]);
				return total + (Number.isFinite(value) ? value : 0);
			}, 0);
		};
		const playlistStyle = win.getComputedStyle(nowPlayingPlaylist);
		const rowGap = Number.parseFloat(playlistStyle.rowGap || playlistStyle.gap);
		const playlistRowsHeight = rows.reduce((total, row) => total + blockSize(row, 48), 0)
			+ Math.max(0, rows.length - 1) * (Number.isFinite(rowGap) ? rowGap : 2);
		const playlistHeight = rows.length
			? playlistRowsHeight + verticalSpace(nowPlayingPlaylist, ['paddingTop', 'paddingBottom'])
			: 0;
		const desiredHeight = verticalSpace(player, ['paddingTop', 'paddingBottom'])
			+ blockSize(nowPlayingGesture, 52)
			+ blockSize(nowPlayingHero, 98)
			+ verticalSpace(nowPlayingHero, ['marginTop', 'marginBottom'])
			+ playlistHeight
			+ (rows.length ? verticalSpace(nowPlayingPlaylist, ['marginTop', 'marginBottom']) : 0)
			+ blockSize(nowPlayingControls, 135)
			+ verticalSpace(nowPlayingControls, ['marginTop', 'marginBottom']);
		const viewportHeight = Math.max(320, win.visualViewport?.height ?? win.innerHeight);
		const full = desiredHeight >= viewportHeight - 16;
		dock.dataset.nowPlayingLayout = full ? 'full' : 'fit';
		// Fit sheets are content-sized by CSS. Keeping a measured pixel height
		// here made transient playlist data and Safari viewport changes leave a
		// stale empty tail until another topic mutation forced a re-measure.
		dock.style.removeProperty('--now-playing-sheet-height');
	};
	const openNowPlaying = () => {
		const currentState = machine.snapshot().dockState;
		if (!mobileViewport || !player.classList.contains('visible') || currentState === 'now-playing') return;
		abortNowPlayingSettle();
		activateModal();
		machine.openNowPlaying();
		syncDom();
		syncNowPlayingLayout();
		animateNowPlayingOpen();
		// Keep the programmatic focus target on the dialog surface. A touch-opened
		// sheet must not make Safari show an unsolicited focus ring on its close
		// button; the non-interactive container suppresses only its own outline in
		// CSS, while every real control still exposes :focus-visible on keyboard
		// navigation.
		focusWithoutScroll(nowPlayingSurface);
	};
	const closeNowPlaying = ({
		preserveDismissTransform = false,
		animateDockReturn = false,
	}: { preserveDismissTransform?: boolean; animateDockReturn?: boolean } = {}) => {
		abortNowPlayingSettle(preserveDismissTransform);
		if (machine.snapshot().dockState !== 'now-playing') return;
		if (preserveDismissTransform) dock.classList.add('now-playing-dismissed');
		machine.closeNowPlaying(readScrollY(win));
		syncDom();
		restoreFocusWithoutRing(releaseModal());
		if (!preserveDismissTransform) return;
		nowPlayingDismissFrame = win.requestAnimationFrame(() => {
			nowPlayingDismissFrame = null;
			dock.classList.remove('now-playing-dismissed');
			if (!animateDockReturn) {
				dock.style.transform = '';
				return;
			}
			// The full panel is already beyond the viewport when its state is
			// restored. Its translated percentage now resolves against the small
			// dock, placing the player/menu just below the screen. Bring that
			// final geometry back once, instead of revealing it in a snapped frame.
			dock.classList.add('now-playing-returning');
			dock.style.transform = 'translateY(0)';
			nowPlayingReturnTimer = setTimeout(() => {
				nowPlayingReturnTimer = null;
				dock.classList.remove('now-playing-returning');
				dock.style.transform = '';
			}, SHEET_SETTLE_MS);
		});
	};
	const restoreExpanded = () => {
		if (!mobileViewport) return;
		machine.restoreExpanded(readScrollY(win));
		syncDom();
	};
	const exclusions = () => ({
		modalActive: Boolean(root.querySelector('.modal-overlay.active')),
		editableFocused: isEditableElement(doc.activeElement),
	});
	const onScroll = () => {
		if (!mobileViewport) return;
		if (restoringHistory) {
			machine.rebase(readScrollY(win));
			syncDom();
			return;
		}
		machine.scrollTo(readScrollY(win), exclusions(), { canCollapse: hasCollapseHeadroom(win) });
		syncDom();
	};
	const historySnapshot = (): MobileBottomDockHistorySnapshot => {
		const snapshot = machine.snapshot();
		return { dockState: snapshot.dockState === 'now-playing' ? 'expanded' : snapshot.dockState, scrollY: readScrollY(win) };
	};
	const restoreHistorySnapshot = (snapshot: MobileBottomDockHistorySnapshot) => {
		if (!mobileViewport) return;
		restoringHistory = true;
		dock.classList.add('mobile-bottom-dock-history-restoring');
		machine.restoreDockState(snapshot.dockState, snapshot.scrollY);
		syncDom();
	};
	const finishHistoryRestore = () => {
		if (!restoringHistory) return;
		// The caller finishes this only after its target viewport commit (the
		// Browse return deliberately waits through its double-rAF scroll).
		// This keeps native/manual restoration scrolls from being interpreted as
		// an up-scroll that flips a persisted compact dock to expanded.
		machine.rebase(readScrollY(win));
		restoringHistory = false;
		dock.classList.remove('mobile-bottom-dock-history-restoring');
		syncDom();
	};
	const onRootClick = (event: Event) => {
		const target = event.target instanceof Element ? event.target : null;
		if (!target) return;
		if (target.closest('[data-open-now-playing="true"]')) {
			event.preventDefault();
			openNowPlaying();
			return;
		}
		if (target.closest('.tab-item')) restoreExpanded();
	};
	const onMiniInfoPointerDown = (event: PointerEvent) => {
		miniInfoStart = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
	};
	const onMiniInfoPointerMove = (event: PointerEvent) => {
		if (!miniInfoStart || event.pointerId !== miniInfoStart.pointerId) return;
		if (Math.hypot(event.clientX - miniInfoStart.x, event.clientY - miniInfoStart.y) >= 8) miniInfoStart.moved = true;
	};
	const onMiniInfoPointerEnd = (event: PointerEvent) => {
		if (miniInfoStart?.pointerId === event.pointerId && miniInfoStart.moved) {
			// A small drag is neither a chapter jump nor a sheet open. The dock's
			// larger upward swipe recognizer remains responsible for opening Now Playing.
			suppressDockClickUntil = Date.now() + SHEET_CLICK_SUPPRESS_MS;
			suppressDockClickSource = miniInfo ?? null;
		}
	};
	const onMiniInfoClick = (event: Event) => {
		if (miniInfoStart?.moved) { miniInfoStart = null; return; }
		miniInfoStart = null;
		const audio = mountedFor(root, 'audio');
		const runtime = getRuntime(root);
		if (!audio?.topicId || !Number.isInteger(audio.chapterIndex) || (audio.chapterIndex ?? 0) < 1 || !runtime) { openNowPlaying(); return; }
		event.preventDefault();
		runtime.activeCourseId = audio.topicId;
		runtime.activeChapterIndex = audio.chapterIndex!;
		showArticle(audio.chapterIndex!, root, runtime.currentPlan ?? 'free');
	};
	const clearBottomOpenGesture = () => {
		bottomOpenGesture = null;
		win.removeEventListener('pointermove', onBottomOpenPointerMove);
		win.removeEventListener('pointerup', onBottomOpenPointerUp);
		win.removeEventListener('pointercancel', onBottomOpenPointerCancel);
	};
	const onDockPointerDown = (event: PointerEvent) => {
		if (!mobileViewport || !player.classList.contains('visible') || machine.snapshot().dockState === 'now-playing') return;
		const target = event.target instanceof Element ? event.target : null;
		if (!target?.closest(bottomOpenGestureScopeSelector) || target.closest(miniOpenGestureExclusionSelector)) return;
		clearBottomOpenGesture();
		bottomOpenGesture = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			source: target.closest(bottomOpenGestureScopeSelector)!,
			horizontal: false,
		};
		win.addEventListener('pointermove', onBottomOpenPointerMove, { passive: false });
		win.addEventListener('pointerup', onBottomOpenPointerUp);
		win.addEventListener('pointercancel', onBottomOpenPointerCancel);
	};
	const onBottomOpenPointerMove = (event: PointerEvent) => {
		if (!bottomOpenGesture || event.pointerId !== bottomOpenGesture.pointerId) return;
		const distanceX = Math.abs(event.clientX - bottomOpenGesture.startX);
		const distanceY = Math.abs(event.clientY - bottomOpenGesture.startY);
		// Reject a lateral intent so a diagonal tab interaction cannot be
		// misclassified as the vertical player-opening gesture.
		if (distanceX > 16 && distanceX > distanceY) {
			bottomOpenGesture.horizontal = true;
			return;
		}
		if (distanceY > SHEET_EDGE_PULL_ARM_PX && distanceY >= distanceX) event.preventDefault();
		const distanceUp = bottomOpenGesture.startY - event.clientY;
		if (bottomOpenGesture.horizontal
			|| distanceUp < MOBILE_BOTTOM_DOCK_THRESHOLDS.miniSwipeUpPx
			|| distanceX > Math.max(24, distanceUp / 2)) return;
		// Commit as soon as the upward intent is unambiguous. Waiting for the
		// release let the page's scroll gesture visibly win first on mobile Safari.
		const gesture = bottomOpenGesture;
		clearBottomOpenGesture();
		suppressDockClickUntil = Date.now() + SHEET_CLICK_SUPPRESS_MS;
		suppressDockClickSource = gesture.source;
		openNowPlaying();
	};
	const onBottomOpenPointerUp = (event: PointerEvent) => {
		if (!bottomOpenGesture || event.pointerId !== bottomOpenGesture.pointerId) return;
		const gesture = bottomOpenGesture;
		clearBottomOpenGesture();
		const distanceUp = gesture.startY - event.clientY;
		const distanceX = Math.abs(event.clientX - gesture.startX);
		if (
			!gesture.horizontal
			&& distanceUp >= MOBILE_BOTTOM_DOCK_THRESHOLDS.miniSwipeUpPx
			&& distanceX <= Math.max(24, distanceUp / 2)
		) {
			// A browser can synthesize a click after a completed pointer gesture.
			// Suppress exactly that follow-up event, otherwise a tab click would
			// immediately call onRootClick() and collapse the modal without
			// releasing its background isolation.
			suppressDockClickUntil = Date.now() + SHEET_CLICK_SUPPRESS_MS;
			suppressDockClickSource = gesture.source;
			openNowPlaying();
		}
	};
	const onBottomOpenPointerCancel = () => {
		clearBottomOpenGesture();
	};
	const onDockClickCapture = (event: MouseEvent) => {
		if (Date.now() > suppressDockClickUntil) {
			suppressDockClickUntil = 0;
			suppressDockClickSource = null;
			return;
		}
		const target = event.target instanceof Element ? event.target : null;
		if (!target || !suppressDockClickSource?.contains(target)) return;
		suppressDockClickUntil = 0;
		suppressDockClickSource = null;
		event.preventDefault();
		event.stopPropagation();
	};
	const clearPlaylistPullGesture = () => {
		playlistPullGesture = null;
		win.removeEventListener('touchmove', onPlaylistTouchMove);
		win.removeEventListener('touchend', onPlaylistTouchEnd);
		win.removeEventListener('touchcancel', onPlaylistTouchCancel);
	};
	const abortNowPlayingSettle = (preserveTransform = false) => {
		if (nowPlayingSettleTimer !== null) {
			clearTimeout(nowPlayingSettleTimer);
			nowPlayingSettleTimer = null;
		}
		if (nowPlayingDismissFrame !== null) {
			win.cancelAnimationFrame(nowPlayingDismissFrame);
			nowPlayingDismissFrame = null;
		}
		if (nowPlayingOpenFrame !== null) {
			win.cancelAnimationFrame(nowPlayingOpenFrame);
			nowPlayingOpenFrame = null;
		}
		if (nowPlayingOpenCommitFrame !== null) {
			win.cancelAnimationFrame(nowPlayingOpenCommitFrame);
			nowPlayingOpenCommitFrame = null;
		}
		if (nowPlayingReturnTimer !== null) {
			clearTimeout(nowPlayingReturnTimer);
			nowPlayingReturnTimer = null;
		}
		nowPlayingDragging = false;
		nowPlayingStartY = null;
		nowPlayingStartX = null;
		nowPlayingGestureSource = null;
		nowPlayingHorizontalGesture = false;
		nowPlayingStartScrollTop = 0;
		nowPlayingDragDy = 0;
		settling = false;
		win.removeEventListener('pointermove', onNowPlayingPointerMove);
		win.removeEventListener('pointerup', onNowPlayingPointerUp);
		win.removeEventListener('pointercancel', onNowPlayingPointerCancel);
		clearPlaylistPullGesture();
		dock.classList.remove('now-playing-opening', 'now-playing-dragging', 'now-playing-settling', 'now-playing-dismissed', 'now-playing-returning');
		if (!preserveTransform) dock.style.transform = '';
	};
	const prefersReducedMotion = () => win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
	// Settle = animated transition of the dock transform to `target`, then
	// the optional callback. The fallback timer mirrors --duration-normal
	// (250ms) so the state swap still lands when transitionend never fires
	// (reduced motion, hidden surface, jsdom). Reduced motion skips the
	// animation and lands the state immediately.
	const settleNowPlayingTo = (target: string, onSettled?: () => void, preserveTransform = false) => {
		settling = true;
		dock.classList.remove('now-playing-dragging');
		const reduceMotion = prefersReducedMotion();
		if (reduceMotion) {
			dock.classList.remove('now-playing-settling');
			if (!preserveTransform) dock.style.transform = '';
			settling = false;
			onSettled?.();
			return;
		}
		dock.classList.add('now-playing-settling');
		dock.style.transform = target;
		nowPlayingSettleTimer = setTimeout(() => {
			nowPlayingSettleTimer = null;
			if (!settling) return;
			settling = false;
			dock.classList.remove('now-playing-settling');
			if (!preserveTransform) dock.style.transform = '';
			onSettled?.();
		}, SHEET_SETTLE_MS);
	};
	const animateNowPlayingOpen = () => {
		if (prefersReducedMotion()) return;
		dock.classList.add('now-playing-opening');
		dock.style.transform = 'translateY(100%)';
		nowPlayingOpenFrame = win.requestAnimationFrame(() => {
			nowPlayingOpenFrame = null;
			nowPlayingOpenCommitFrame = win.requestAnimationFrame(() => {
				nowPlayingOpenCommitFrame = null;
				if (machine.snapshot().dockState !== 'now-playing' || !dock.classList.contains('now-playing-opening')) return;
				dock.classList.remove('now-playing-opening');
				settleNowPlayingTo('translateY(0)');
			});
		});
	};
	const beginNowPlayingDismiss = () => {
		if (machine.snapshot().dockState !== 'now-playing') return;
		if (dock.classList.contains('now-playing-opening')) abortNowPlayingSettle();
		if (settling) return;
		settleNowPlayingTo('translateY(100%)', finishNowPlayingDismiss, true);
	};
	const finishNowPlayingDismiss = () => {
		if (prefersReducedMotion()) {
			closeNowPlaying();
			return;
		}
		closeNowPlaying({ preserveDismissTransform: true, animateDockReturn: true });
	};
	const finishNowPlayingDismissGesture = ({
		canceled,
		dy,
		horizontalGesture,
		velocity,
		source,
	}: { canceled: boolean; dy: number; horizontalGesture: boolean; velocity: number; source: Element | null }) => {
		dock.classList.remove('now-playing-dragging');
		if (canceled || horizontalGesture) {
			if (dy > 0) settleNowPlayingTo('translateY(0)');
			else dock.style.transform = '';
			return;
		}
		if (dy <= 0) {
			dock.style.transform = '';
			return;
		}
		const dismiss = shouldDismissSheet({ canceled, horizontal: horizontalGesture, dy, velocity });
		if (dismiss) {
			suppressNowPlayingClickUntil = Date.now() + SHEET_CLICK_SUPPRESS_MS;
			suppressNowPlayingClickSource = source;
			settleNowPlayingTo('translateY(100%)', finishNowPlayingDismiss, true);
			return;
		}
		// A short pull is not a dismiss intent. Restore the sheet and leave
		// taps on its content available to their normal controls.
		settleNowPlayingTo('translateY(0)');
	};
	const onNowPlayingPointerDown = (event: PointerEvent) => {
		if (machine.snapshot().dockState !== 'now-playing') return;
		if (dock.classList.contains('now-playing-opening')) abortNowPlayingSettle();
		if (settling) return;
		const target = event.target instanceof Element ? event.target : null;
		if (target?.closest(nowPlayingDismissGestureExclusionSelector)) return;
		// The playlist uses a touch fallback below so native pan-y scrolling can
		// continue until its top edge without racing PointerEvent cancellation.
		const playlist = target?.closest<HTMLElement>('.wp-playlist');
		if (playlist) return;
		nowPlayingStartY = event.clientY;
		nowPlayingStartX = event.clientX;
		nowPlayingGestureSource = target?.closest('button, [data-proto-click]') ?? target;
		nowPlayingHorizontalGesture = false;
		nowPlayingStartScrollTop = nowPlayingScrollRegion?.scrollTop ?? 0;
		nowPlayingDragDy = 0;
		lastMoveY = event.clientY;
		lastMoveAt = Date.now();
		moveVelocity = 0;
		nowPlayingDragging = true;
		dock.classList.add('now-playing-dragging');
		win.addEventListener('pointermove', onNowPlayingPointerMove, { passive: true });
		win.addEventListener('pointerup', onNowPlayingPointerUp);
		win.addEventListener('pointercancel', onNowPlayingPointerCancel);
	};
	const onNowPlayingPointerMove = (event: PointerEvent) => {
		if (!nowPlayingDragging || nowPlayingStartY === null || nowPlayingStartX === null) return;
		const distanceX = Math.abs(event.clientX - nowPlayingStartX);
		const distanceY = Math.abs(event.clientY - nowPlayingStartY);
		if (distanceX > 16 && distanceX > distanceY) {
			nowPlayingHorizontalGesture = true;
			dock.style.transform = '';
			return;
		}
		if (nowPlayingHorizontalGesture) return;
		const rawDy = event.clientY - nowPlayingStartY;
		const nextScrollTop = nowPlayingStartScrollTop - rawDy;
		if (nowPlayingScrollRegion && nextScrollTop >= 0) {
			nowPlayingScrollRegion.scrollTop = nextScrollTop;
			nowPlayingDragDy = 0;
			dock.style.transform = '';
		} else {
			if (nowPlayingScrollRegion) nowPlayingScrollRegion.scrollTop = 0;
			const pullDy = Math.max(0, -nextScrollTop);
			nowPlayingDragDy = pullDy;
			dock.style.transform = `translateY(${pullDy}px)`;
		}
		const now = Date.now();
		const dt = now - lastMoveAt;
		if (dt > 0) moveVelocity = (event.clientY - lastMoveY) / dt;
		lastMoveY = event.clientY;
		lastMoveAt = now;
	};
	const onNowPlayingPointerUp = (event: PointerEvent) => {
		finishNowPlayingDrag(false, event.clientY);
	};
	const onNowPlayingPointerCancel = () => {
		finishNowPlayingDrag(true);
	};
	const touchForIdentifier = (touches: TouchList, identifier: number) => Array.from(touches).find((touch) => touch.identifier === identifier) ?? null;
	const onPlaylistTouchStart = (event: TouchEvent) => {
		if (machine.snapshot().dockState !== 'now-playing') return;
		if (dock.classList.contains('now-playing-opening')) abortNowPlayingSettle();
		if (settling) return;
		const target = event.target instanceof Element ? event.target : null;
		const playlist = target?.closest<HTMLElement>('.wp-playlist');
		const scrollRegion = nowPlayingScrollRegion;
		if (!target || !playlist || !scrollRegion) return;
		const touch = event.changedTouches[0];
		if (!touch) return;
		clearPlaylistPullGesture();
		playlistPullGesture = {
			identifier: touch.identifier,
			startX: touch.clientX,
			startY: touch.clientY,
			source: target.closest('button, [data-proto-click]') ?? target,
			scrollRegion,
			active: false,
			horizontal: false,
			edgeStartY: scrollRegion.scrollTop <= 0 ? touch.clientY : null,
			lastY: touch.clientY,
			lastAt: Date.now(),
			velocity: 0,
		};
		win.addEventListener('touchmove', onPlaylistTouchMove, { passive: false });
		win.addEventListener('touchend', onPlaylistTouchEnd);
		win.addEventListener('touchcancel', onPlaylistTouchCancel);
	};
	const onPlaylistTouchMove = (event: TouchEvent) => {
		const gesture = playlistPullGesture;
		if (!gesture) return;
		const touch = touchForIdentifier(event.touches, gesture.identifier) ?? touchForIdentifier(event.changedTouches, gesture.identifier);
		if (!touch) return;
		const distanceX = Math.abs(touch.clientX - gesture.startX);
		const distanceY = Math.abs(touch.clientY - gesture.startY);
		if (distanceX > 16 && distanceX > distanceY) {
			gesture.horizontal = true;
			clearPlaylistPullGesture();
			return;
		}
		if (!gesture.active) {
			if (gesture.scrollRegion.scrollTop > 0) {
				gesture.edgeStartY = null;
				return;
			}
			if (gesture.edgeStartY === null) {
				// Native pan-y owned the gesture until the list reached its first
				// row. Start counting dismissal distance only from that edge so the
				// same continuous pull can scroll first and close second.
				gesture.edgeStartY = touch.clientY;
				gesture.lastY = touch.clientY;
				gesture.lastAt = Date.now();
				return;
			}
			const dy = touch.clientY - gesture.edgeStartY;
			if (dy <= SHEET_EDGE_PULL_ARM_PX) return;
			// The list is at its top edge and the user has clearly pulled down.
			// From this point the non-passive listener owns the gesture, preventing
			// native rubber-banding from cancelling the dismiss sequence.
			gesture.active = true;
			dock.classList.add('now-playing-dragging');
		}
		event.preventDefault();
		const dy = touch.clientY - (gesture.edgeStartY ?? gesture.startY);
		const now = Date.now();
		const elapsed = now - gesture.lastAt;
		if (elapsed > 0) gesture.velocity = (touch.clientY - gesture.lastY) / elapsed;
		gesture.lastY = touch.clientY;
		gesture.lastAt = now;
		dock.style.transform = `translateY(${Math.max(0, dy)}px)`;
	};
	const onPlaylistTouchEnd = (event: TouchEvent) => {
		const gesture = playlistPullGesture;
		if (!gesture) return;
		const touch = touchForIdentifier(event.changedTouches, gesture.identifier);
		if (!gesture.active || !touch) {
			clearPlaylistPullGesture();
			return;
		}
		event.preventDefault();
		const dy = Math.max(0, touch.clientY - (gesture.edgeStartY ?? gesture.startY));
		const { horizontal, velocity, source } = gesture;
		clearPlaylistPullGesture();
		finishNowPlayingDismissGesture({ canceled: false, dy, horizontalGesture: horizontal, velocity, source });
	};
	const onPlaylistTouchCancel = () => {
		const gesture = playlistPullGesture;
		if (!gesture) return;
		const dy = gesture.active ? Math.max(0, gesture.lastY - (gesture.edgeStartY ?? gesture.startY)) : 0;
		const { horizontal, velocity, source } = gesture;
		clearPlaylistPullGesture();
		if (gesture.active) finishNowPlayingDismissGesture({ canceled: true, dy, horizontalGesture: horizontal, velocity, source });
	};
	const finishNowPlayingDrag = (canceled: boolean, upClientY?: number) => {
		if (!nowPlayingDragging) return;
		nowPlayingDragging = false;
		win.removeEventListener('pointermove', onNowPlayingPointerMove);
		win.removeEventListener('pointerup', onNowPlayingPointerUp);
		win.removeEventListener('pointercancel', onNowPlayingPointerCancel);
		// A fast flick can arrive as pointerdown -> pointerup with no
		// pointermove in between (and the browser may coalesce moves), so
		// derive the final offset from the release position, falling back to
		// the last tracked move.
		const rawDy = nowPlayingStartY !== null && upClientY !== undefined ? upClientY - nowPlayingStartY : 0;
		const dy = nowPlayingStartY !== null && upClientY !== undefined
			? Math.max(0, rawDy - nowPlayingStartScrollTop)
			: nowPlayingDragDy;
		const horizontalGesture = nowPlayingHorizontalGesture;
		const gestureSource = nowPlayingGestureSource;
		if (!canceled && !horizontalGesture && Math.abs(rawDy) > SHEET_EDGE_PULL_ARM_PX) {
			suppressNowPlayingClickUntil = Date.now() + SHEET_CLICK_SUPPRESS_MS;
			suppressNowPlayingClickSource = gestureSource;
		}
		nowPlayingStartY = null;
		nowPlayingStartX = null;
		nowPlayingGestureSource = null;
		nowPlayingHorizontalGesture = false;
		nowPlayingStartScrollTop = 0;
		nowPlayingDragDy = 0;
		finishNowPlayingDismissGesture({ canceled, dy, horizontalGesture, velocity: moveVelocity, source: gestureSource });
	};
	const onNowPlayingClickCapture = (event: MouseEvent) => {
		if (Date.now() > suppressNowPlayingClickUntil) {
			suppressNowPlayingClickUntil = 0;
			suppressNowPlayingClickSource = null;
			return;
		}
		const target = event.target instanceof Element ? event.target : null;
		if (!target || !suppressNowPlayingClickSource?.contains(target)) return;
		suppressNowPlayingClickUntil = 0;
		suppressNowPlayingClickSource = null;
		event.preventDefault();
		event.stopPropagation();
	};
	const onProgressStart = () => {
		machine.setProgressDragging(true, readScrollY(win));
	};
	const onProgressEnd = () => {
		machine.setProgressDragging(false, readScrollY(win));
	};
	const onViewportChange = (event: MediaQueryListEvent) => {
		mobileViewport = event.matches;
		if (!mobileViewport && machine.snapshot().dockState === 'now-playing') closeNowPlaying();
		machine.rebase(readScrollY(win));
		syncDom();
		syncNowPlayingLayout();
	};
	const onVisualViewportResize = () => syncNowPlayingLayout();
	const onMutations = (records: MutationRecord[]) => {
		const videoActive = Boolean(videoFullscreen?.classList.contains('active'));
		machine.setVideoFullscreen(videoActive, readScrollY(win));
		// Opening the video fullscreen from the now-playing panel must tear
		// the panel's modal down: activateModal() inerted every root child -
		// including #videoFullscreen itself - and the video open path never
		// released it. An inert fullscreen surface swallows every pointer
		// event, so tapping the video could not toggle the controls, and the
		// stale inert set survived even after Back closed the video (page
		// behind the dock stayed dead + scroll-locked). Closing the panel
		// here releases inert from all root children and clears the lock, so
		// the video starts fully interactive no matter which entry opened it.
		if (videoActive && machine.snapshot().dockState === 'now-playing') closeNowPlaying();
		const playerVisibilityChanged = records.some((record) => record.target === player);
		if (playerVisibilityChanged && player.classList.contains('visible') && !videoActive && machine.snapshot().dockState !== 'now-playing') {
			machine.restoreExpanded(readScrollY(win));
		}
		syncDom();
	};

	win.addEventListener('scroll', onScroll, { passive: true });
	root.addEventListener('click', onRootClick);
	miniInfo?.addEventListener('click', onMiniInfoClick);
	miniInfo?.addEventListener('pointerdown', onMiniInfoPointerDown);
	miniInfo?.addEventListener('pointermove', onMiniInfoPointerMove);
	miniInfo?.addEventListener('pointerup', onMiniInfoPointerEnd);
	miniInfo?.addEventListener('pointercancel', onMiniInfoPointerEnd);
	dock.addEventListener('pointerdown', onDockPointerDown);
	dock.addEventListener('click', onDockClickCapture, true);
	nowPlayingSurface?.addEventListener('pointerdown', onNowPlayingPointerDown);
	nowPlayingSurface?.addEventListener('click', onNowPlayingClickCapture, true);
	nowPlayingSurface?.addEventListener('touchstart', onPlaylistTouchStart, { passive: true });
	closeButton?.addEventListener('click', beginNowPlayingDismiss);
	progress?.addEventListener('pointerdown', onProgressStart);
	progress?.addEventListener('pointerup', onProgressEnd);
	progress?.addEventListener('pointercancel', onProgressEnd);
	mobileQuery?.addEventListener?.('change', onViewportChange);
	const observer = new MutationObserver(onMutations);
	observer.observe(player, { attributes: true, attributeFilter: ['class'] });
	if (videoFullscreen) observer.observe(videoFullscreen, { attributes: true, attributeFilter: ['class'] });
	const playlistObserver = new MutationObserver(syncNowPlayingLayout);
	if (nowPlayingPlaylist) playlistObserver.observe(nowPlayingPlaylist, { childList: true, subtree: true });
	win.visualViewport?.addEventListener('resize', onVisualViewportResize);
	syncDom();

	const controller: MobileBottomDockController = {
		machine,
		snapshot: machine.snapshot,
		openNowPlaying,
		closeNowPlaying,
		historySnapshot,
		restoreHistorySnapshot,
		finishHistoryRestore,
		destroy() {
			if (destroyed) return;
			destroyed = true;
			const returnFocus = releaseModal();
			observer.disconnect();
			playlistObserver.disconnect();
			abortNowPlayingSettle();
			win.removeEventListener('scroll', onScroll);
			root.removeEventListener('click', onRootClick);
			miniInfo?.removeEventListener('click', onMiniInfoClick);
			miniInfo?.removeEventListener('pointerdown', onMiniInfoPointerDown);
			miniInfo?.removeEventListener('pointermove', onMiniInfoPointerMove);
			miniInfo?.removeEventListener('pointerup', onMiniInfoPointerEnd);
			miniInfo?.removeEventListener('pointercancel', onMiniInfoPointerEnd);
			dock.removeEventListener('pointerdown', onDockPointerDown);
			dock.removeEventListener('click', onDockClickCapture, true);
			clearBottomOpenGesture();
			nowPlayingSurface?.removeEventListener('pointerdown', onNowPlayingPointerDown);
			nowPlayingSurface?.removeEventListener('click', onNowPlayingClickCapture, true);
			nowPlayingSurface?.removeEventListener('touchstart', onPlaylistTouchStart);
			clearPlaylistPullGesture();
			closeButton?.removeEventListener('click', beginNowPlayingDismiss);
			progress?.removeEventListener('pointerdown', onProgressStart);
			progress?.removeEventListener('pointerup', onProgressEnd);
			progress?.removeEventListener('pointercancel', onProgressEnd);
			mobileQuery?.removeEventListener?.('change', onViewportChange);
			win.visualViewport?.removeEventListener('resize', onVisualViewportResize);
			doc.documentElement.classList.remove('mobile-now-playing-open');
			doc.body.classList.remove('mobile-now-playing-open');
			appLayout?.removeAttribute('data-mobile-dock-state');
			appLayout?.removeAttribute('data-mobile-player-visible');
			dock.style.removeProperty('--now-playing-sheet-height');
			restorePosition(player, playerPosition);
			restorePosition(tabNav, tabPosition);
			dock.remove();
			restoreFocusWithoutRing(returnFocus);
			mountedControllers.delete(root);
		},
	};
	mountedControllers.set(root, controller);
	return controller;
}

function rememberPosition(element: Element): ElementPosition {
	return { parent: element.parentNode!, nextSibling: element.nextSibling };
}

function restorePosition(element: Element, position: ElementPosition): void {
	position.parent.insertBefore(element, position.nextSibling);
}

function readScrollY(win: Window): number {
	return Math.max(0, win.scrollY || win.document.documentElement.scrollTop || 0);
}

function isEditableElement(element: Element | null): boolean {
	return Boolean(element?.matches('input, textarea, select, [contenteditable]:not([contenteditable="false"])'));
}

function focusWithoutScroll(element: HTMLElement | null): void {
	if (!element?.isConnected) return;
	try {
		element.focus({ preventScroll: true });
	} catch {
		element.focus();
	}
}

function inertController(): MobileBottomDockController {
	const machine = createMobileBottomDockStateMachine();
	return {
		machine,
		snapshot: machine.snapshot,
		openNowPlaying() {},
		closeNowPlaying() {},
		historySnapshot: () => ({ dockState: 'expanded', scrollY: 0 }),
		restoreHistorySnapshot() {},
		finishHistoryRestore() {},
		destroy() {},
	};
}

export function persistMobileBottomDockHistory(root: HTMLElement | Document): void {
	const controller = controllerFor(root);
	if (!controller || typeof window === 'undefined' || !window.history) return;
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
	try {
		window.history.replaceState({ ...currentState, mobileBottomDock: controller.historySnapshot() }, '', window.location.href);
	} catch {
		// History persistence is an enhancement; the active controller remains
		// authoritative until a real Back/Forward entry is available.
	}
}

export function mobileBottomDockHistorySnapshot(root: HTMLElement | Document): MobileBottomDockHistorySnapshot | null {
	return controllerFor(root)?.historySnapshot() ?? null;
}

export function restoreMobileBottomDockHistory(root: HTMLElement | Document, state: unknown): MobileBottomDockHistorySnapshot | null {
	if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
	const value = (state as Record<string, unknown>).mobileBottomDock;
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const snapshot = value as Record<string, unknown>;
	if ((snapshot.dockState !== 'expanded' && snapshot.dockState !== 'compact') || typeof snapshot.scrollY !== 'number' || !Number.isFinite(snapshot.scrollY)) return null;
	const normalized = { dockState: snapshot.dockState, scrollY: Math.max(0, snapshot.scrollY) } as MobileBottomDockHistorySnapshot;
	controllerFor(root)?.restoreHistorySnapshot(normalized);
	return normalized;
}

export function restoreMobileBottomDockSnapshot(root: HTMLElement | Document, snapshot: MobileBottomDockHistorySnapshot): void {
	controllerFor(root)?.restoreHistorySnapshot(snapshot);
}

export function finishMobileBottomDockHistoryRestore(root: HTMLElement | Document): void {
	controllerFor(root)?.finishHistoryRestore();
}

function controllerFor(root: HTMLElement | Document): MobileBottomDockController | undefined {
	if (root instanceof HTMLElement) return mountedControllers.get(root);
	const host = root.querySelector<HTMLElement>('#root');
	return host ? mountedControllers.get(host) : undefined;
}
